import * as Toast from "@radix-ui/react-toast";
import {
  Trash,
  Shield,
  ShieldOff,
  Mail,
  User,
  Crown,
  Briefcase,
} from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { User as UserType } from "@/types/user";

import { Button } from "../ui/button";
import { UserEditDialog } from "./UserEditDialog";

interface UserCardProps extends UserType {
  blocked: boolean;
  onUserUpdate: () => void;
}

export function UserCard({
  id,
  name,
  email,
  role,
  blocked,
  onUserUpdate,
}: UserCardProps) {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const roleConfig = {
    ADMIN: {
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-700",
      label: "Administrador",
      icon: Crown,
    },
    BROKER: {
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      textColor: "text-green-700",
      label: "Corretor",
      icon: Briefcase,
    },
  };

  const config = roleConfig[role];
  const RoleIcon = config.icon;

  async function handleDeleteUser() {
    setLoading(true);
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/users/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }
      onUserUpdate();
      setToastType("success");
      setToastMessage("Usuário excluído com sucesso!");
      setToastOpen(true);
    } catch (error) {
      console.error("Failed to delete user:", error);
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao excluir o usuário!");
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  }

  async function handleToggleBlock() {
    setLoading(true);
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/users/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ blocked: !blocked }),
      });

      if (!response.ok) {
        throw new Error("Failed to update user");
      }
      onUserUpdate();
      setToastType("success");
      setToastMessage(
        `Usuário ${!blocked ? "bloqueado" : "desbloqueado"} com sucesso!`,
      );
      setToastOpen(true);
    } catch (error) {
      console.error("Failed to update user:", error);
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao atualizar o usuário!");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full">
      <Toast.Provider swipeDirection="right">
        <div
          className={`
          relative overflow-hidden rounded-2xl border bg-white/80 shadow-lg backdrop-blur-sm
          transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
          ${blocked ? "border-red-200 bg-red-50/50" : "border-gray-200/50"}
        `}
        >
          {/* Status Indicator */}
          <div
            className={`absolute left-0 right-0 top-0 h-1 ${
              blocked
                ? "bg-gradient-to-r from-red-500 to-red-600"
                : "bg-gradient-to-r from-green-500 to-green-600"
            }`}
          />

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Avatar */}
                <div
                  className={`
                  relative h-16 w-16 rounded-2xl bg-gradient-to-r ${config.color} 
                  flex items-center justify-center shadow-lg
                `}
                >
                  <User className="h-8 w-8 text-white" />
                  {blocked && (
                    <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500">
                      <ShieldOff className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-xl font-bold text-gray-900">{name}</h3>
                    <div
                      className={`
                      inline-flex items-center rounded-full bg-gradient-to-r px-3 py-1 text-sm
                      font-medium ${config.color} text-white shadow-sm
                    `}
                    >
                      <RoleIcon className="mr-1 h-4 w-4" />
                      {config.label}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm">{email}</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    {blocked ? (
                      <div className="flex items-center space-x-1 text-red-600">
                        <ShieldOff className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Acesso Bloqueado
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 text-green-600">
                        <Shield className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Acesso Liberado
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <UserEditDialog
                  id={id}
                  currentName={name}
                  currentEmail={email}
                  currentRole={role}
                  currentBlocked={blocked}
                  onUserUpdate={onUserUpdate}
                />

                <Button
                  onClick={handleToggleBlock}
                  disabled={loading}
                  className={`
                    h-10 rounded-xl px-4 transition-all duration-200
                    ${
                      blocked
                        ? "border border-green-300 bg-green-100 text-green-700 hover:bg-green-200"
                        : "border border-red-300 bg-red-100 text-red-700 hover:bg-red-200"
                    }
                  `}
                >
                  {loading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : blocked ? (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Desbloquear
                    </>
                  ) : (
                    <>
                      <ShieldOff className="mr-2 h-4 w-4" />
                      Bloquear
                    </>
                  )}
                </Button>

                <Dialog open={openConfirm} onOpenChange={setOpenConfirm}>
                  <DialogTrigger asChild>
                    <Button className="h-10 rounded-xl border border-red-300 bg-red-100 px-4 text-red-700 transition-all duration-200 hover:bg-red-200">
                      <Trash className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-2xl bg-white p-6 shadow-2xl sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold text-gray-900">
                        Confirmar Exclusão
                      </DialogTitle>
                      <DialogDescription className="text-gray-600">
                        Tem certeza que deseja excluir este usuário? Esta ação
                        não pode ser desfeita.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                      <Button
                        className="rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
                        onClick={() => setOpenConfirm(false)}
                        disabled={loading}
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="rounded-xl bg-red-600 text-white hover:bg-red-700"
                        onClick={handleDeleteUser}
                        disabled={loading}
                      >
                        {loading ? "Excluindo..." : "Excluir"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </div>

        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={5000}
          className={`
            fixed bottom-4 right-4 rounded-xl border p-4 shadow-lg backdrop-blur-sm
            ${
              toastType === "success"
                ? "border-green-400 bg-green-500/90 text-white"
                : "border-red-400 bg-red-500/90 text-white"
            }
          `}
        >
          <Toast.Description className="font-medium">
            {toastMessage}
          </Toast.Description>
          <Toast.Close className="absolute right-2 top-2 text-white/80 hover:text-white" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
